import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '../ui/Button.jsx'
import { IconButton } from '../ui/IconButton.jsx'
import {
  useForm,
  getLeadFormFieldDefinitions,
} from '../../context/FormContext.jsx'
import { cn } from '../../lib/cn.js'
import { t } from '../../lib/widgetLocale.js'

const DEFAULT_INPUT_BY_TYPE = {
  email: 'email',
  tel: 'tel',
  text: 'text',
}

/** Resolve admin form theme (flat keys + legacy `config.colors` nest). */
export function resolveLeadFormThemeColors(config) {
  const nested =
    config.colors && typeof config.colors === 'object' ? config.colors : {}
  return {
    headlineColor: config.headlineColor || nested.headline || '',
    descriptionColor: config.descriptionColor || nested.description || '',
    buttonColor: config.buttonColor || nested.button || '',
    buttonTextColor: config.buttonTextColor || nested.buttonText || '',
    fieldLabelColor: config.fieldLabelColor || nested.label || '',
    inputBorderColor: config.inputBorderColor || nested.inputBorder || '',
    inputBackgroundColor: config.inputBackgroundColor || nested.inputBackground || '',
  }
}

/**
 * Lead form rendered inside the chat panel (not a viewport modal).
 * Reads active form from `FormContext`. Colors come from form `config`
 * with fallbacks to widget CSS variables.
 */
export function LeadFormInPanel({ onSubmit, conf }) {
  const {
    activeForm,
    isFormOpen,
    closeForm,
    formData,
    formErrors,
    updateFormData,
    handleFormSubmit,
    isSubmitted,
    setSubmitted,
  } = useForm()
  const [submitting, setSubmitting] = useState(false)
  const closeTimer = useRef(null)
  const panelRef = useRef(null)
  const centeringShellRef = useRef(null)

  const clampFormVertical = useCallback(() => {
    const shell = centeringShellRef.current
    const card = panelRef.current
    if (!shell || !card) return
    card.style.transform = ''
    // Force layout read after clearing transform
    void card.offsetHeight
    const pad = 8
    const s = shell.getBoundingClientRect()
    const c = card.getBoundingClientRect()
    let dy = 0
    if (c.bottom > s.bottom - pad) dy = s.bottom - pad - c.bottom
    if (c.top + dy < s.top + pad) dy += s.top + pad - (c.top + dy)
    if (dy !== 0) card.style.transform = `translateY(${dy}px)`
  }, [])

  useEffect(() => {
    return () => {
      if (closeTimer.current) window.clearTimeout(closeTimer.current)
    }
  }, [])

  useEffect(() => {
    if (!isFormOpen || !activeForm || isSubmitted) return
    const root = panelRef.current
    if (!root) return
    const focusable = root.querySelector(
      'input:not([type="hidden"]), textarea, select, button[type="submit"]',
    )
    if (focusable && typeof focusable.focus === 'function') {
      queueMicrotask(() => focusable.focus())
    }
  }, [isFormOpen, activeForm?.id, isSubmitted])

  useEffect(() => {
    if (!isFormOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        closeForm()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isFormOpen, closeForm])

  useLayoutEffect(() => {
    if (!isFormOpen || !activeForm) return
    const shell = centeringShellRef.current
    const card = panelRef.current
    if (!shell || !card) return

    clampFormVertical()
    const ro = new ResizeObserver(() => {
      clampFormVertical()
    })
    ro.observe(shell)
    ro.observe(card)
    window.addEventListener('resize', clampFormVertical)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', clampFormVertical)
      if (card) card.style.transform = ''
    }
  }, [
    isFormOpen,
    activeForm?.id,
    isSubmitted,
    submitting,
    clampFormVertical,
  ])

  if (!activeForm || !isFormOpen) return null

  const fieldDefs = getLeadFormFieldDefinitions(activeForm, conf?.siteLanguage)
  const config = activeForm.config || {}
  const settings = activeForm.settings || {}
  const theme = resolveLeadFormThemeColors(config)
  const lang = conf?.siteLanguage

  const title = config.headline || activeForm.title || t('form.title', lang)
  const description =
    config.description || activeForm.description || ''
  const submitLabel =
    config.buttonText || activeForm.submitLabel || t('form.submit', lang)
  const successMessage =
    settings.successMessage || t('form.successMessage', lang)

  const headlineColor =
    theme.headlineColor || 'var(--color-surface-foreground)'
  const descriptionColor =
    theme.descriptionColor || 'var(--color-muted-foreground)'
  const labelColor =
    theme.fieldLabelColor || 'var(--color-surface-foreground)'
  const buttonBg =
    theme.buttonColor || 'var(--color-brand)'
  const buttonFg =
    theme.buttonTextColor || 'var(--color-brand-foreground)'
  const inputBorder =
    theme.inputBorderColor || 'var(--color-border)'
  const inputBg =
    theme.inputBackgroundColor || 'var(--color-surface)'

  const submit = async (event) => {
    event.preventDefault()
    if (submitting) return
    const result = handleFormSubmit(formData, activeForm, lang)
    if (!result.success) return
    setSubmitting(true)
    try {
      await onSubmit?.({ form: activeForm, data: formData, conf })
      setSubmitted(true)
      if (settings.redirectUrl) {
        window.location.href = settings.redirectUrl
        return
      }
      if (closeTimer.current) window.clearTimeout(closeTimer.current)
      closeTimer.current = window.setTimeout(() => {
        setSubmitted(false)
        closeForm()
      }, 2000)
    } catch (err) {
      console.error(err)
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass = cn(
    'w-full rounded-lg border px-3 py-2 text-sm outline-none transition-shadow',
    'text-[var(--color-surface-foreground)] placeholder:text-[var(--color-muted-foreground)]/70',
    'focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-surface)]',
  )

  const inputStyle = {
    borderColor: inputBorder,
    backgroundColor: inputBg,
    ...(theme.buttonColor ? { ['--tw-ring-color']: theme.buttonColor } : {}),
  }

  return (
    <>
      <div
        className={cn(
          'absolute inset-0 z-[15] cursor-default bg-black/40 backdrop-blur-[1.5px]',
          'dark:bg-black/55',
        )}
        aria-hidden="true"
        onClick={closeForm}
      />
      <div
        ref={centeringShellRef}
        className="pointer-events-none absolute inset-0 z-20 flex min-h-0 items-center justify-center p-2 sm:p-3"
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ometrics-lead-form-title"
          className={cn(
            'ometrics-lead-form-card pointer-events-auto flex max-h-full min-h-0 w-full max-w-[min(420px,calc(100%-0.25rem))] flex-col overflow-hidden rounded-xl border border-[var(--color-border)]',
            'bg-[var(--color-surface)] shadow-[0_16px_48px_rgba(0,0,0,0.22)]',
            'dark:shadow-[0_16px_48px_rgba(0,0,0,0.55)]',
          )}
        >
      <div
        className={cn(
          'flex shrink-0 items-start gap-3 border-b border-[var(--color-border)]/80',
          'bg-gradient-to-b from-[var(--color-muted)]/35 to-transparent px-4 pb-4 pt-3',
        )}
      >
        <div className="min-w-0 flex-1 space-y-1">
          <h2
            id="ometrics-lead-form-title"
            className="text-lg font-semibold leading-snug tracking-tight sm:text-xl"
            style={{ color: headlineColor }}
          >
            {title}
          </h2>
          {description && !isSubmitted ? (
            <p
              className="text-sm leading-relaxed sm:text-[15px]"
              style={{ color: descriptionColor }}
            >
              {description}
            </p>
          ) : null}
        </div>
        <IconButton
          aria-label={t('form.close', lang)}
          onClick={closeForm}
          className="shrink-0 text-[var(--color-muted-foreground)] hover:text-[var(--color-surface-foreground)]"
        >
          <X className="h-4 w-4" />
        </IconButton>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-3">
        {isSubmitted ? (
          <div className="py-8 text-center">
            <div
              className="text-lg font-semibold"
              style={{ color: headlineColor }}
            >
              {t('form.thankYou', lang)}
            </div>
            <p
              className="mt-2 text-sm leading-relaxed"
              style={{ color: descriptionColor }}
            >
              {successMessage}
            </p>
          </div>
        ) : (
          <form className="flex flex-col gap-3" onSubmit={submit} noValidate>
            {fieldDefs.map((field) => {
              const error = formErrors[field.name]
              const id = `ometrics-field-${field.id || field.name}`
              const label = field.label || field.name
              const requiredMark = field.required ? (
                <span className="text-red-600"> *</span>
              ) : null
              const labelStyle = { color: labelColor }

              if (field.inputType === 'textarea') {
                return (
                  <label key={field.id} htmlFor={id} className="text-sm">
                    <span className="mb-1.5 block font-medium" style={labelStyle}>
                      {label}
                      {requiredMark}
                    </span>
                    <textarea
                      id={id}
                      rows={3}
                      value={formData[field.name] || ''}
                      onChange={(e) =>
                        updateFormData({ [field.name]: e.target.value })
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                    {error ? (
                      <span className="mt-1 block text-xs text-red-600">
                        {error}
                      </span>
                    ) : null}
                  </label>
                )
              }

              if (field.inputType === 'select') {
                const opts = field.options || []
                return (
                  <label key={field.id} htmlFor={id} className="text-sm">
                    <span className="mb-1.5 block font-medium" style={labelStyle}>
                      {label}
                      {requiredMark}
                    </span>
                    <select
                      id={id}
                      value={formData[field.name] ?? ''}
                      onChange={(e) =>
                        updateFormData({ [field.name]: e.target.value })
                      }
                      className={inputClass}
                      style={inputStyle}
                    >
                      {!field.required ? <option value="">—</option> : null}
                      {opts.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label || o.value}
                        </option>
                      ))}
                    </select>
                    {error ? (
                      <span className="mt-1 block text-xs text-red-600">
                        {error}
                      </span>
                    ) : null}
                  </label>
                )
              }

              if (field.inputType === 'radio') {
                const opts = field.options || []
                return (
                  <fieldset key={field.id} className="text-sm">
                    <legend
                      className="mb-1.5 font-medium"
                      style={labelStyle}
                    >
                      {label}
                      {requiredMark}
                    </legend>
                    <div className="flex flex-col gap-1.5">
                      {opts.map((o) => (
                        <label
                          key={o.value}
                          className="flex cursor-pointer items-center gap-2"
                          style={labelStyle}
                        >
                          <input
                            type="radio"
                            name={field.name}
                            value={o.value}
                            checked={formData[field.name] === o.value}
                            onChange={() =>
                              updateFormData({ [field.name]: o.value })
                            }
                          />
                          <span>{o.label || o.value}</span>
                        </label>
                      ))}
                    </div>
                    {error ? (
                      <span className="mt-1 block text-xs text-red-600">
                        {error}
                      </span>
                    ) : null}
                  </fieldset>
                )
              }

              if (field.inputType === 'checkbox' && field.allowMultiple) {
                const opts = field.options || []
                const selected = Array.isArray(formData[field.name])
                  ? formData[field.name]
                  : []
                const toggle = (val) => {
                  const set = new Set(selected)
                  if (set.has(val)) set.delete(val)
                  else set.add(val)
                  updateFormData({ [field.name]: Array.from(set) })
                }
                return (
                  <fieldset key={field.id} className="text-sm">
                    <legend
                      className="mb-1.5 font-medium"
                      style={labelStyle}
                    >
                      {label}
                      {requiredMark}
                    </legend>
                    <div className="flex flex-col gap-1.5">
                      {opts.map((o) => (
                        <label
                          key={o.value}
                          className="flex cursor-pointer items-center gap-2"
                          style={labelStyle}
                        >
                          <input
                            type="checkbox"
                            checked={selected.includes(o.value)}
                            onChange={() => toggle(o.value)}
                          />
                          <span>{o.label || o.value}</span>
                        </label>
                      ))}
                    </div>
                    {error ? (
                      <span className="mt-1 block text-xs text-red-600">
                        {error}
                      </span>
                    ) : null}
                  </fieldset>
                )
              }

              if (field.inputType === 'checkbox') {
                return (
                  <label
                    key={field.id}
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 text-sm"
                    style={labelStyle}
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={Boolean(formData[field.name])}
                      onChange={(e) =>
                        updateFormData({ [field.name]: e.target.checked })
                      }
                    />
                    <span>
                      {label}
                      {requiredMark}
                    </span>
                    {error ? (
                      <span className="ml-auto block text-xs text-red-600">
                        {error}
                      </span>
                    ) : null}
                  </label>
                )
              }

              const htmlType =
                DEFAULT_INPUT_BY_TYPE[field.inputType] ||
                DEFAULT_INPUT_BY_TYPE.text
              const isWebsiteField = field.inputType === 'url'
              return (
                <label key={field.id} htmlFor={id} className="text-sm">
                  <span className="mb-1.5 block font-medium" style={labelStyle}>
                    {label}
                    {requiredMark}
                  </span>
                  <input
                    id={id}
                    type={htmlType}
                    inputMode={isWebsiteField ? 'url' : undefined}
                    autoComplete={isWebsiteField ? 'url' : undefined}
                    value={formData[field.name] || ''}
                    onChange={(e) =>
                      updateFormData({ [field.name]: e.target.value })
                    }
                    className={inputClass}
                    style={inputStyle}
                  />
                  {error ? (
                    <span className="mt-1 block text-xs text-red-600">
                      {error}
                    </span>
                  ) : null}
                </label>
              )
            })}
            <div className="mt-2 flex flex-col gap-2">
              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  'inline-flex h-11 min-h-[44px] w-full items-center justify-center rounded-full',
                  'px-5 text-sm font-semibold tracking-wide transition-opacity',
                  'disabled:pointer-events-none disabled:opacity-60',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                  'focus-visible:ring-offset-[var(--color-surface)]',
                )}
                style={{
                  backgroundColor: buttonBg,
                  color: buttonFg,
                  ...(theme.buttonColor
                    ? { ['--tw-ring-color']: theme.buttonColor }
                    : {}),
                }}
              >
                {submitting ? t('form.sending', lang) : submitLabel}
              </button>
              <Button
                variant="ghost"
                onClick={closeForm}
                type="button"
                className="w-full sm:self-end"
              >
                {t('form.cancel', lang)}
              </Button>
            </div>
          </form>
        )}
      </div>
        </div>
      </div>
    </>
  )
}
